import { makeStyles } from "makeStyles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { permissionsTableStyles } from "../styles";
import SingleValues from "../SingleValues";
import { useState, useMemo } from "react";
import { TableSortOrder, getComparator, SimpleSortableTableHeadProps, SortableTableHead } from "../SortableTableHead";
import { PermissionUser } from "../models";
import UsersInas from "./UsersInas";
import UsersPermissions from "./UsersPermissions";
import ClosableDialogTitle from "common/ClosableDialogTitle";
import { useAppConfig } from "util/AppConfig";
import { getAdminRoutes } from "Routes";
import UserScreens from "./UserAdminScreens";

const useStyles = makeStyles()((theme) => ({
  ...permissionsTableStyles(theme),
  nameCell: {
    width: "220px"
  },
  screensCell: {
    width: "50px"
  },
  groupsCell: {
    width: "220px"
  },
  assignmentsCell: {
    width: "700px"
  }
}));

interface Props {
  users?: PermissionUser[];
}

const AppAccessUsers: React.FunctionComponent<Props> = (props) => {
  const { classes } = useStyles();

  const [order, setOrder] = useState<TableSortOrder>("asc");
  const [orderBy, setOrderBy] = useState<keyof PermissionUser>("name");

  const [screensDialogOpenFor, setScreensDialogOpenFor] = useState<PermissionUser | null>(null);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof PermissionUser) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const displayRows = useMemo(() => [...(props.users ?? [])].sort(getComparator(order, orderBy)), [order, orderBy, props.users]);

  function UsersTableHead({ id, label, className }: SimpleSortableTableHeadProps<PermissionUser>) {
    return (
      <SortableTableHead
        className={className}
        headCell={{
          id,
          label
        }}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />
    );
  }

  return (
    <>
      <TableContainer component={Paper} className={classes.topLevelTableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <UsersTableHead id="name" label="Name" className={classes.nameCell} />
              <TableCell className={classes.groupsCell}>Groups</TableCell>
              <TableCell className={classes.assignmentsCell}>INAs</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell className={classes.screensCell}>Screens</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>
                  {" "}
                  {user.groups?.length === 0 ? (
                    "--"
                  ) : user.groups?.length === 1 ? (
                    user.groups[0]
                  ) : (
                    <details>
                      <summary>{user.groups!.length} Groups:</summary>
                      <SingleValues values={user.groups} />
                    </details>
                  )}
                </TableCell>
                <TableCell>
                  {" "}
                  {user.inas?.length === 0 ? (
                    "--"
                  ) : (
                    <details>
                      <summary>{user.inas!.length} INAs:</summary>
                      <UsersInas inas={user.inas} />
                    </details>
                  )}
                </TableCell>
                <TableCell>
                  {" "}
                  {user.appRoles?.length === 0 ? (
                    "--"
                  ) : (
                    <details>
                      <summary>
                        {user.appRoles!.length} Permission{user.appRoles!.length > 1 ? "s" : ""}:
                      </summary>
                      <UsersPermissions permissions={user.appRoles} />
                    </details>
                  )}
                </TableCell>
                <TableCell>
                  <Button onClick={() => setScreensDialogOpenFor(user)}>🔎</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={screensDialogOpenFor ? true : false} maxWidth="sm" fullWidth scroll="paper">
        <ClosableDialogTitle onClose={() => setScreensDialogOpenFor(null)}>
          Admin Nav Items for {screensDialogOpenFor?.name}
        </ClosableDialogTitle>
        <DialogContent>
          <UserScreens user={screensDialogOpenFor} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreensDialogOpenFor(null)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppAccessUsers;
